import { AFrameScene } from '../lib/aframe-components'
import './main.css'

declare let React: any
declare let AFRAME: any
declare let THREE: any

// ─── Override shader chromakey CDN con versione custom (threshold + smoothness) ───
// Eseguito al caricamento del modulo, PRIMA che AFrameScene inietti la scena
if (typeof AFRAME !== 'undefined') {
  // Rimuovi la versione CDN (aframe-chromakey-material@1.1.4) che non ha threshold/smoothness
  if (AFRAME.shaders && AFRAME.shaders.chromakey) {
    delete AFRAME.shaders.chromakey
  }

  AFRAME.registerShader('chromakey', {
    schema: {
      src: { type: 'selector' },
      color: { type: 'vec3', default: { x: 0.02, y: 0.08, z: 0.99 } },
      threshold: { type: 'number', default: 0.15 },
      smoothness: { type: 'number', default: 0.1 }
    },

    init(data: any) {
      let colorValue
      if (data.color && typeof data.color === 'object' && data.color.x !== undefined) {
        colorValue = new THREE.Vector3(data.color.x, data.color.y, data.color.z)
      } else {
        colorValue = new THREE.Vector3(0.02, 0.08, 0.99)
      }

      this.material = new THREE.ShaderMaterial({
        uniforms: {
          src: { value: new THREE.Texture() },
          color: { value: colorValue },
          threshold: { value: data.threshold || 0.15 },
          smoothness: { value: data.smoothness || 0.1 }
        },
        vertexShader: `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform sampler2D src;
          uniform vec3 color;
          uniform float threshold;
          uniform float smoothness;
          varying vec2 vUv;

          void main() {
            vec4 texColor = texture2D(src, vUv);
            float dist = distance(texColor.rgb, color);
            if (dist < threshold) {
              discard;
            }
            float alpha = smoothstep(threshold, threshold + smoothness, dist);
            gl_FragColor = vec4(texColor.rgb, alpha);
          }
        `,
        transparent: true,
        side: THREE.DoubleSide
      })

      // Crea VideoTexture dal video element (data.src è l'elemento DOM risolto dal selector)
      this._setupVideoTexture(data.src)
    },

    _setupVideoTexture(videoEl: any) {
      if (!videoEl) return
      const tex = new THREE.VideoTexture(videoEl)
      tex.minFilter = THREE.LinearFilter
      tex.magFilter = THREE.LinearFilter
      tex.format = THREE.RGBAFormat
      tex.crossOrigin = 'anonymous'
      this.material.uniforms.src.value = tex
    },

    update(data: any) {
      if (!this.material || !this.material.uniforms) return

      // Se il video element cambia, ricrea la VideoTexture
      if (data.src && data.src.tagName === 'VIDEO') {
        this._setupVideoTexture(data.src)
      }

      if (data.color && typeof data.color === 'object' && data.color.x !== undefined) {
        this.material.uniforms.color.value.set(data.color.x, data.color.y, data.color.z)
      }

      if (data.threshold !== undefined) {
        this.material.uniforms.threshold.value = data.threshold
      }
      if (data.smoothness !== undefined) {
        this.material.uniforms.smoothness.value = data.smoothness
      }
    }
  })
  console.log('🎭 Shader chromakey custom registrato (threshold + smoothness)')
}

// Parametri hardcoded dell'ologramma
const VIDEO_PATH = 'https://iscag-ei.it/wp-content/materiale/ologramma.mp4'
const CAMERA_POSITION = '0 4 6'
const PLANE_SCALE = '14 14 14'
const PLANE_POSITION = '0 0 -8'
const CHROMA_COLOR = '0.0 1.0 0.4'
const GROUND_WIDTH = '1000'
const GROUND_HEIGHT = '1000'
const RISE_HEIGHT = '0.915'
const AUTOPLAY = false
const LOOP = false

// Scena A-Frame con ologramma chromakey
// Route pubblica: /ologramma — nessun token richiesto
const SCENE_HTML = `
<style>
/* Splash ARWEB — appare istantaneamente, rimosso da custom-qr-loading dopo 1s */
#arweb-splash {
    position: fixed;
    inset: 0;
    z-index: 99999;
    background: url('https://iscag-ei.it/wp-content/materiale/splashscreenarweb.png') center/cover no-repeat, #1a1a2e;
}

/* Nascondi branding 8thWall */
.foreground-image.poweredby-img {
    display: none !important;
}

#cta {
    z-index: 2;
    position: absolute;
    right: 6vh;
    left: 5.5vh;
    top: 40vh;
    font-family: Nunito,sans-serif;
    color: #fff;
    font-weight: 600;
    text-decoration: none;
    padding: .8em 1.6em;
    border-radius: 50px;
    text-transform: uppercase;
    letter-spacing: 1px;
    text-align: center;
    background: linear-gradient(90deg,#7611b7,#c86dd7);
}
</style>

<div id="arweb-splash"></div>

<a id="cta">Clicca qui per vedere l'ologramma</a>

<a-scene
  xrextras-gesture-detector
  xrextras-loading
  custom-qr-loading="splashImage: https://iscag-ei.it/wp-content/materiale/splashscreenarweb.png; showBackButton: false"
  xrextras-runtime-error
  renderer="colorManagement: true; physicallyCorrectLights: true;"
  xrweb="
    allowedDevices: any;
    defaultEnvironmentFogIntensity: 0.5; 
    defaultEnvironmentFloorColor: #FFF;
    defaultEnvironmentSkyBottomColor: #B4C4CC; 
    defaultEnvironmentSkyTopColor: #5ac8fa;
    defaultEnvironmentSkyGradientStrength: 0.5;
  ">

  <a-assets>
    <video id="alpha-video" playsinline webkit-playsinline preload="auto" crossorigin="anonymous" loop="${LOOP}" src="${VIDEO_PATH}#t=0.001"></video>
    <img id="blobShadow" src="https://iscag-ei.it/wp-content/materiale/ologramma-milone/blob-shadow.png" crossorigin="anonymous">
  </a-assets>

  <a-camera id="camera" position="${CAMERA_POSITION}" raycaster="objects: .cantap" cursor="fuse: false; rayOrigin: mouse;"></a-camera>

  <a-light type="ambient"></a-light>

  <a-entity
    id="alphaVideo"
    class="cantap"
    custom-hold-drag="riseHeight: ${RISE_HEIGHT}"
    play-video-correct="video: #alpha-video; autoplay: ${AUTOPLAY}"
    material="shader: chromakey; src: #alpha-video; color: ${CHROMA_COLOR}; threshold: 0.70; smoothness: 0.0; side: double; depthTest: true;"
    geometry="primitive: plane; height: 1; width: 1.78;"
    xrextras-two-finger-rotate
    xrextras-pinch-scale
    look-at="target: #camera; tilt: false"
    position="${PLANE_POSITION}"
    scale="${PLANE_SCALE}">
      <a-circle
        position="0 -4 0"
        rotation="-90 0 0"
        scale="1.5 0.6 1"
        radius="0.5"
        material="src: #blobShadow; opacity: 0.65; roughness: 0.8; metalness: 0;">
      </a-circle>
  </a-entity>

  <a-plane id="ground" rotation="-90 0 0" width="${GROUND_WIDTH}" height="${GROUND_HEIGHT}" material="shader: shadow" shadow></a-plane>

</a-scene>
`

const OlogrammaExperience = () => {
  return (
    <AFrameScene sceneHtml={SCENE_HTML} />
  )
}

export { OlogrammaExperience }
